import React from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/constants';
import PersonModal from './PersonModal';


export interface Address {
  id: number;
  nickname: string;
  address_line1: string;
  city: string;
  state: string;
  zip: string;
}

interface PersonSectionProps {
  addresses: Address[];
}

export default function PersonSection({ addresses = [] }: PersonSectionProps) {
  const [people, setPeople] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [showModal, setShowModal] = React.useState<boolean>(false);
  const [editId, setEditId] = React.useState<number | null>(null);
  const [form, setForm] = React.useState<any>({ name: '', email: '', nickname: '', is_a: '', birth_day: '', birth_month: '', birth_year: '', address_id: '', preferences: {} });
  const [editForm, setEditForm] = React.useState<any>({ name: '', email: '', nickname: '', is_a: '', birth_day: '', birth_month: '', birth_year: '', address_id: '', preferences: {} });
  const [error, setError] = React.useState<string | null>(null);
  const [editError, setEditError] = React.useState<string | null>(null);

  React.useEffect(() => {
    fetchPeople();
    // eslint-disable-next-line
  }, []);

  async function fetchPeople() {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/people/`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setPeople(res.data);
    } catch (err) {
      // handle error
    }
    setLoading(false);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        ...form,
        address_id: form.address_id && form.address_id !== '' ? Number(form.address_id) : null
      };
      await axios.post(`${API_BASE_URL}/api/person/`, payload, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setShowModal(false);
      setForm({ name: '', email: '', nickname: '', is_a: '', birth_day: '', birth_month: '', birth_year: '', address_id: '', preferences: {} });
      fetchPeople();
    } catch (err: any) {
      if (err.response && err.response.data) {
        const errors = err.response.data;
        setError(
          typeof errors === 'string'
            ? errors
            : Object.entries(errors).map(([field, msgs]) => {
                if (typeof msgs === 'object') {
                  return `${field}: ${JSON.stringify(msgs)}`;
                }
                return `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`;
              }).join(' | ')
        );
      } else {
        setError('An error occurred.');
      }
    }
  }

  return (
    <section style={{ marginBottom: 40 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700 }}>People</h2>
        <button onClick={() => setShowModal(true)} style={{ padding: '8px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600 }}>New Person</button>
      </div>
      {loading ? (
        <div>Loading...</div>
      ) : (
        <>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            {people.map((person: any) => {
              // Address display
              let addressDisplay = <span style={{ color: '#ef4444', fontWeight: 700, fontSize: 14 }}>no address</span>;
              let addressId = person.address;
              if (typeof addressId === 'object' && addressId !== null && 'nickname' in addressId) {
                // If backend returns nested address object
                addressDisplay = <span style={{ color: '#22c55e', fontSize: 14, fontWeight: 400 }}>{addressId.nickname}</span>;
                addressId = addressId.id;
              } else if (addressId) {
                // If backend returns just the id, look up in addresses prop
                const found = addresses.find(a => a.id === addressId);
                if (found) {
                  addressDisplay = <span style={{ color: '#22c55e', fontSize: 14, fontWeight: 400 }}>{found.nickname}</span>;
                }
              }
              // Likes count
              const likesCount = person.preferences && person.preferences.likes ? person.preferences.likes.length : 0;
              return (
                <li key={person.id} style={{ background: '#f3f4f6', marginBottom: 8, padding: 12, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 16, color: '#222', fontWeight: 400 }}>
                    <strong style={{ fontSize: 16 }}>
                      {person.name}
                      {person.nickname ? ` (${person.nickname})` : ''}
                    </strong>
                    {likesCount > 0 && (
                      <span style={{ color: '#2563eb', fontSize: 16, fontWeight: 400, marginLeft: 8 }}>
                        {likesCount} like{likesCount !== 1 ? 's' : ''}
                      </span>
                    )}
                    {addressDisplay && (
                      <span style={{ fontSize: 16, color: '#222', fontWeight: 400, marginLeft: 8 }}>{addressDisplay}</span>
                    )}
                    {person.dates && person.dates.length > 0 && (
                      <ul style={{ margin: '4px 0 0 0', padding: 0, listStyle: 'none' }}>
                        {person.dates.map((date: any, i: number) => {
                          // Only show if month is present
                          if (!date.month) return null;
                          const today = new Date();
                          const currentYear = today.getFullYear();
                          let eventDate;
                          if (date.day) {
                            eventDate = new Date(currentYear, Number(date.month) - 1, Number(date.day));
                          } else {
                            // Only month specified: use 1st of the month
                            eventDate = new Date(currentYear, Number(date.month) - 1, 1);
                          }
                          if (eventDate < today) {
                            eventDate.setFullYear(currentYear + 1);
                          }
                          const diffMs = eventDate.getTime() - today.getTime();
                          const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
                          if (diffDays < 14) {
                            return (
                              <li key={i} style={{ fontSize: 14, color: '#555', fontWeight: 400 }}>
                                {diffDays} day{diffDays !== 1 ? 's' : ''} until {date.label || 'Date'}
                              </li>
                            );
                          } else {
                            const diffWeeks = Math.ceil(diffDays / 7);
                            return (
                              <li key={i} style={{ fontSize: 14, color: '#555', fontWeight: 400 }}>
                                {diffWeeks} week{diffWeeks !== 1 ? 's' : ''} until {date.label || 'Date'}
                              </li>
                            );
                          }
                        })}
                      </ul>
                    )}
                  </span>
                  <span style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => {
                      setEditId(person.id);
                      setEditForm({
                        name: person.name || '',
                        email: person.email || '',
                        nickname: person.nickname || '',
                        is_a: person.is_a || '',
                        birth_day: person.birth_day || '',
                        birth_month: person.birth_month || '',
                        birth_year: person.birth_year || '',
                        address_id: addressId || null,
                        preferences: person.preferences || {},
                        dates: Array.isArray(person.dates) ? person.dates : [],
                      });
                      setEditError(null);
                    }} style={{ padding: '4px 10px', background: '#fbbf24', color: '#333', border: 'none', borderRadius: 6, fontWeight: 600 }}>Edit</button>
                    <button onClick={async () => { await axios.delete(`${API_BASE_URL}/api/person/${person.id}/`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }); fetchPeople(); }} style={{ padding: '4px 10px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600 }}>Delete</button>
                  </span>
                </li>
              );
            })}
          </ul>
      {editId !== null && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <PersonModal
            mode="edit"
            form={editForm}
            setForm={setEditForm}
            error={editError}
            onSubmit={async e => {
              e.preventDefault();
              setEditError(null);
              try {
                const payload = {
                  ...editForm,
                  address_id: editForm.address_id && editForm.address_id !== '' ? Number(editForm.address_id) : null
                };
                await axios.patch(`${API_BASE_URL}/api/person/${editId}/`, payload, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                setEditId(null);
                fetchPeople();
              } catch (err: any) {
                if (err.response && err.response.data) {
                  const errors = err.response.data;
                  setEditError(
                    typeof errors === 'string'
                      ? errors
                      : Object.entries(errors).map(([field, msgs]) => {
                          if (typeof msgs === 'object') {
                            return `${field}: ${JSON.stringify(msgs)}`;
                          }
                          return `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`;
                        }).join(' | ')
                  );
                } else {
                  setEditError('An error occurred.');
                }
              }
            }}
            onCancel={() => setEditId(null)}
            addresses={addresses}
          />
        </div>
      )}
        </>
      )}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <PersonModal
            mode="create"
            form={form}
            setForm={setForm}
            error={error}
            onSubmit={handleCreate}
            onCancel={() => setShowModal(false)}
            addresses={addresses}
          />
        </div>
      )}
    </section>
  );
}
